import { EmberClueType, isEmberClueType, TutorialType } from "game-common/models"

// The bookkeeping and the geometry behind the first-run ember camp's six onscreen clues: small
// pointer-and-text boxes that name, in order, the controls the camp's first fire is built with (open
// the pack, switch to crafting, craft the campfire, find it in the pack, Ready it, close the pack).
//
// Pure of pixi on purpose, like crafting_layout.ts beside it -- partly so it can be tested directly
// (gui/tutorial.ts cannot be imported from a unit test: it reaches the sprite graph, which is
// circular), and partly because the awkward part of this feature is not the drawing.
//
// THE AWKWARD PART. Arming and anchoring are separate events, in either order:
//   * ARMED   -- the server said this clue's beat is live. It arms the whole craft chain in one go,
//                at the moment the Lord asks for a fire, because that is when it knows.
//   * ANCHORED -- the control it points at is on screen, and here is where. Two of those three
//                controls are inside an inventory panel the player has not opened yet.
// So a clue waits for its control, is drawn the moment one appears, is redrawn on every rebuild of the
// panel holding it, stops pointing when the panel closes (while staying armed), and holds for its own
// timeout whatever the player does. It is taken down for good by that timeout, by the player
// dismissing it, or by the whole lesson being marked seen.

// How long a clue holds when the server doesn't say. A clue is not a notice like the overworld popups
// (7s): it labels a control the player is being sent to go and find, so it has to outlast the hunt.
export const CLUE_TIMEOUT_MS = 45000

// Space between a clue's text box and the control it points at. The note's notch reaches most of
// the way across it; what is left is clear air between the point and the brackets round the control.
export const CLUE_LEADER_GAP = 22

// The notch the note points with (gui/clue_callout.ts draws it). Its geometry lives here, pure, so it
// can be tested without a canvas.
export const CLUE_NOTE_CHAMFER = 5
export const CLUE_NOTCH_HALF = 6
export const CLUE_NOTCH_LEN = 8
// The point stops this far short of the control, so it never stabs into the brackets.
const NOTCH_CLEARANCE = 6
// Shorter than this and a notch is a nick in the rim, not a point: leave it off.
const NOTCH_MIN_LEN = 3
// How far the tip may lean off square when the note had to be pushed along to stay on screen. Past
// about 45 degrees it stops reading as a point and starts reading as a tear in the board.
const NOTCH_MAX_LEAN = 8

// How far the box may be pushed down from the top of its host before it is left where it is.
export const CLUE_BOX_MIN_Y = 4
// ...and how close to its host's left edge it may sit. A host that is the whole screen wants a small
// margin; a right-docked panel passes its own (negative) bound.
export const CLUE_BOX_MIN_X = 8

// What each clue says. One imperative per control. No em dashes, same rule as the Lord's spoken lines.
export const CLUE_TEXT: Record<EmberClueType, string[]> = {
    // The one clue that is not about the fire. The dial it points at is the whole reason the line
    // lands: a countdown is a DEADLINE, and a deadline is what makes speed something the player
    // wants, in a camp where nothing yet makes it something they need. Hence the second line -- it
    // names what the dial is for, rather than claiming the two skirmishers can outrun anybody.
    "ember-run": ["Hold SHIFT to run.", "Be back at the fire before this runs out."],
    "ember-ready": ["Your axe is ready here.", "Anything on this bar can be held."],
    "ember-wood": ["Ready this wood.", "Strike the campfire with it to light it."],
    "ember-wood-items": ["Open your pack to ready your wood."],
    "ember-wood-pack": ["Choose the wood for its actions."],
    "ember-wood-use": ["Select Ready, then close your pack."],
    "ember-items": ["Open your pack.", "The wood you cut is in there."],
    "ember-planks-items": ["Open your pack and craft planks for the roof."],
    "ember-crafting": ["Switch to crafting."],
    "ember-planks-recipe": ["Craft planks from your wood.", "These are the materials for your shelter."],
    "ember-wall-recipe": ["Craft wood walls from your planks."],
    "ember-wall-pack": ["Choose a wall in your pack."],
    "ember-wall-use": ["Select Ready, then close your pack.", "Place walls on three sides of the fire. Leave a way in."],
    "ember-roof-recipe": ["Craft wood roof sections.", "Check the recipe for the materials you need."],
    "ember-roof-pack": ["Choose a roof section in your pack."],
    "ember-roof-use": ["Select Ready, then close your pack.", "Highlight at least a 2×2 square over the fire and wait for it to build."],
    "ember-recipe": ["Craft a campfire out of the wood."],
    "ember-pack": ["Here it is, in your pack."],
    "ember-use": ["Select Ready."],
    "ember-close": ["Close your pack.", "Then aim at the ground and click to place the fire."],
}

// The six controls in the order the player meets them, which is also how a clue is RETIRED. Drawing
// one of these controls is proof the step before it was taken: the inventory panel cannot draw its
// crafting toggle unless the pack is open, cannot draw a recipe card unless crafting is showing, cannot
// draw a campfire slot unless one was crafted, cannot draw a Use button unless the item was picked,
// and reports its close button as the clued control only while the campfire is readied.
// So each anchor report satisfies the clue ahead of it and the chain advances by itself, with no need
// for the server to be told what the player clicked.
//
// The last clue is the exception: nothing drawn on screen proves the campfire left the pack to become a
// fire on the ground, so the server takes the set down when it sees that (ember-tutorial.js).
export const CLUE_ORDER: EmberClueType[] = [
    // "ember-ready" is deliberately NOT in this chain. It names the ready bar when the axe is handed
    // over, which is a beat of its own with nothing before or after it -- putting it in the order
    // would have the pack's clue retire it, which is not what taking the axe means. It ends on its own
    // timeout, or with the rest when the lesson is marked seen.
    //
    // Nor is "ember-run", for a stronger reason: it is raised a wave into the run, long after this
    // chain is done, and the only thing that retires it is the player actually running (see
    // Player's Shift handler, which calls satisfyClue).
    "ember-items",
    "ember-crafting",
    "ember-recipe",
    "ember-pack",
    "ember-use",
    "ember-close",
]

// Which clue, if any, is proven done by having drawn this one's control.
export const clueSatisfiedByDrawing = (tutorialType: EmberClueType): EmberClueType | undefined =>
    tutorialType === "ember-planks-recipe" || tutorialType === "ember-wall-recipe" || tutorialType === "ember-roof-recipe" ? "ember-crafting"
        : tutorialType === "ember-wall-pack" ? "ember-wall-recipe"
        : tutorialType === "ember-wall-use" ? "ember-wall-pack"
        : tutorialType === "ember-roof-pack" ? "ember-roof-recipe"
        : tutorialType === "ember-roof-use" ? "ember-roof-pack"
        : tutorialType === "ember-wood-pack" ? "ember-wood-items"
        : tutorialType === "ember-wood-use" ? "ember-wood-pack"
        : tutorialType === "ember-wood" ? "ember-wood-use"
        : CLUE_ORDER[CLUE_ORDER.indexOf(tutorialType) - 1]

// What an incoming SHOW_TUTORIAL means, decided in one place so the suppression rule is legible.
//
// `suppressed` is latched true in every ember room (GuiManager.roomUpdated) to keep the OVERWORLD
// popups -- which the intro campaign fires off its own client-side triggers -- from interrupting a
// camp. It must NOT reach the clues: those are the camp's own tutorial, armed a beat at a time by the
// camp's own director, and suppressing them would suppress the very thing the latch protects.
export type TutorialRoute = "clear" | "clue" | "popup" | "suppressed"

export const routeTutorial = (tutorialType: TutorialType | null | undefined, suppressed: boolean): TutorialRoute => {
    if (!tutorialType) {
        return "clear"
    }
    if (isEmberClueType(tutorialType)) {
        return "clue"
    }
    return suppressed ? "suppressed" : "popup"
}

// Where a clue's box and its pointer go, relative to the control.
//
// The box sits to the LEFT of the control, vertically centred on it. Left is the only side with room:
// every clued control is either in the top-right HUD row or inside the right-docked inventory panel.
// `boxMinX` is the left edge the box may not cross, in the host container's own coordinates -- a
// docked panel's container starts hundreds of pixels into the screen, so its clues legitimately sit at
// a negative x. `leader` is the length of the hairline from the box back to the control, and is 0 when
// the clamp left no clear gap to draw one in.
export interface ClueBoxLayout {
    boxX: number
    boxY: number
    leader: number
}

// Which side of its control a clue's box sits on.
//
// "left" is the default and suits everything in the top-right HUD row or the right-docked panel.
// "above" is for a control with nothing to its left to sit in: the READY BAR is at the bottom-left
// corner of the screen, so a box beside it gets clamped straight back on top of the thing it is
// pointing at.
// "below" keeps a top-of-panel control's clue clear of the mobile HUD chips.
// "right" is for the COUNTDOWN DIAL, which is in the top-left corner: "left" clamps the box straight
// back onto it, and the slot directly under it is taken by the camp's fire gauge (gui_manager's
// placeEmberFireGauge). The empty top band between the dial and the HUD chips is the only room
// there is, and it reads left-to-right off the dial into the words.
export type CluePlacement = "left" | "above" | "below" | "right"

export const clueBoxLayout = ({
    anchorX,
    anchorY,
    anchorW = 0,
    anchorH,
    boxW,
    boxH,
    boxMinX = CLUE_BOX_MIN_X,
    boxMaxX,
    placement = "left",
}: {
    anchorX: number
    anchorY: number
    anchorW?: number
    anchorH: number
    boxW: number
    boxH: number
    boxMinX?: number
    boxMaxX?: number
    placement?: CluePlacement
}): ClueBoxLayout => {
    if (placement === "right") {
        // Out to the right of the control and level with it. `leader` is the horizontal run back to
        // it. Clamped on the FAR edge rather than the near one: the dial sits in a corner so there is
        // always room beside it, but a long line on a narrow screen can still overhang, and a box
        // pushed back left would land on the dial.
        const wanted = anchorX + anchorW + CLUE_LEADER_GAP
        const boxX = boxMaxX === undefined ? wanted : Math.min(wanted, Math.max(anchorX + anchorW, boxMaxX - boxW))
        const boxY = Math.max(CLUE_BOX_MIN_Y, anchorY + anchorH / 2 - boxH / 2)
        return { boxX, boxY, leader: Math.max(0, boxX - (anchorX + anchorW)) }
    }
    if (placement === "below") {
        const boxX = Math.max(boxMinX, anchorX + anchorW / 2 - boxW / 2)
        const boxY = Math.max(CLUE_BOX_MIN_Y, anchorY + anchorH + CLUE_LEADER_GAP)
        return { boxX, boxY, leader: Math.max(0, boxY - (anchorY + anchorH)) }
    }
    if (placement === "above") {
        // Centred over the control and sitting on top of it. `leader` is the vertical drop back down
        // to it, and is 0 when the clamp left no clear gap to draw one in.
        const boxX = Math.max(boxMinX, anchorX + anchorW / 2 - boxW / 2)
        const boxY = Math.max(CLUE_BOX_MIN_Y, anchorY - CLUE_LEADER_GAP - boxH)
        return { boxX, boxY, leader: Math.max(0, anchorY - (boxY + boxH)) }
    }
    const boxX = Math.max(boxMinX, anchorX - CLUE_LEADER_GAP - boxW)
    const boxY = Math.max(CLUE_BOX_MIN_Y, anchorY + anchorH / 2 - boxH / 2)
    return { boxX, boxY, leader: Math.max(0, anchorX - (boxX + boxW)) }
}

export type ClueEdge = "left" | "right" | "top" | "bottom"

// Where a note's notch is: the edge it leaves from, and its tip, in the note's own coordinates.
export interface ClueNotch {
    edge: ClueEdge
    tipX: number
    tipY: number
}

// Where the notch's base sits along its edge: opposite the control's centre (`along`, measured along
// that edge), clamped clear of the chamfered corners.
export const notchBase = (edgeLength: number, along: number) => {
    const min = CLUE_NOTE_CHAMFER + CLUE_NOTCH_HALF + 1
    const max = edgeLength - CLUE_NOTE_CHAMFER - CLUE_NOTCH_HALF - 1
    if (max < min) return edgeLength / 2
    return Math.max(min, Math.min(max, along))
}

// The notch a note placed at (boxX, boxY) points at its control with, or null when the note ended up
// too close to it (clamped against the screen edge) for a point to fit. The notch leaves from the
// edge facing the control, opposite its centre where the edge allows, and leans toward it otherwise.
export const clueNotch = ({
    placement,
    boxX,
    boxY,
    boxW,
    boxH,
    anchorX,
    anchorY,
    anchorW,
    anchorH,
}: {
    placement: CluePlacement
    boxX: number
    boxY: number
    boxW: number
    boxH: number
    anchorX: number
    anchorY: number
    anchorW: number
    anchorH: number
}): ClueNotch | null => {
    // The control's centre, in the note's own coordinates.
    const cx = anchorX + anchorW / 2 - boxX
    const cy = anchorY + anchorH / 2 - boxY
    const reach = (gap: number) => Math.min(CLUE_NOTCH_LEN, gap - NOTCH_CLEARANCE)
    const aim = (edgeLength: number, along: number) => {
        const base = notchBase(edgeLength, along)
        return Math.max(base - NOTCH_MAX_LEAN, Math.min(base + NOTCH_MAX_LEAN, along))
    }
    const notch = (len: number, edge: ClueEdge, tipX: number, tipY: number) =>
        len < NOTCH_MIN_LEN ? null : { edge, tipX, tipY }
    if (placement === "above") {
        const len = reach(anchorY - (boxY + boxH))
        return notch(len, "bottom", aim(boxW, cx), boxH + len)
    }
    if (placement === "below") {
        const len = reach(boxY - (anchorY + anchorH))
        return notch(len, "top", aim(boxW, cx), -len)
    }
    if (placement === "right") {
        const len = reach(boxX - (anchorX + anchorW))
        return notch(len, "left", -len, aim(boxH, cy))
    }
    const len = reach(anchorX - (boxX + boxW))
    return notch(len, "right", boxW + len, aim(boxH, cy))
}

// How to take a drawn clue down. `fade` when it is going for good (done, dismissed, expired); a
// clue merely being redrawn, or whose control has just gone, comes down at once.
export type ClueStop = (opts?: { fade?: boolean }) => void

// Draws one clue at an anchor and hands back how to take it down again, or undefined if it could not
// be drawn. The anchor type is the caller's business: this module only stores it and hands it back.
export type ClueRenderer<A> = (tutorialType: EmberClueType, anchor: A) => ClueStop | undefined

export interface EmberClueDirectorProps<A> {
    draw: ClueRenderer<A>
    // Anchors nothing has to report, because their control is part of the permanent HUD rather than a
    // panel that comes and goes. Resolved on every draw attempt, since the HUD's own visibility moves.
    builtInAnchor?: (tutorialType: EmberClueType) => A | undefined
    defaultTimeoutMs?: number
}

export class EmberClueDirector<A> {
    private props: EmberClueDirectorProps<A>
    // Armed, with each clue's expiry timer.
    private armed: Map<EmberClueType, ReturnType<typeof setTimeout>> = new Map()
    // Where each clue's control was last drawn.
    private anchors: Map<EmberClueType, A> = new Map()
    // What is currently on screen, and how to retract it.
    private drawn: Map<EmberClueType, ClueStop> = new Map()

    constructor(props: EmberClueDirectorProps<A>) {
        this.props = props
    }

    // The server says this clue's beat is live. Draws it now if its control is already on screen;
    // otherwise remembers it, so it appears the moment the control does.
    arm = (tutorialType: EmberClueType, timeoutMs?: number) => {
        if (this.armed.has(tutorialType)) {
            return
        }
        const hold = timeoutMs && timeoutMs > 0 ? timeoutMs : this.props.defaultTimeoutMs || CLUE_TIMEOUT_MS
        this.armed.set(
            tutorialType,
            setTimeout(() => this.disarm(tutorialType), hold),
        )
        this.refresh(tutorialType)
    }

    // Whatever drew one of the clued controls says where it landed. Called again on every rebuild of
    // the panel it lives in, which is why this redraws rather than skipping: an inventory update
    // throws the whole content tree away, clue and all.
    registerAnchor = (tutorialType: EmberClueType, anchor: A) => {
        this.anchors.set(tutorialType, anchor)
        this.refresh(tutorialType)
    }

    // The control has gone (its panel closed, or this rebuild no longer draws it). The clue stays
    // ARMED -- reopening the panel puts it back -- but nothing is left pointing at nothing.
    clearAnchor = (tutorialType: EmberClueType) => {
        this.anchors.delete(tutorialType)
        this.retract(tutorialType)
    }

    // Every clue down and disarmed. Sent by the server the moment the tutorial counts as seen, and
    // done locally on a room change.
    clearAll = (preserveAnchors = false) => {
        Array.from(this.armed.keys()).forEach(type => this.disarm(type))
        Array.from(this.drawn.keys()).forEach(type => this.retract(type))
        if (!preserveAnchors) this.anchors.clear()
    }

    // Taken down for good rather than merely undrawn: an expired clue, or one the player dismissed,
    // must not reappear on the next rebuild of the panel it pointed at.
    disarm = (tutorialType: EmberClueType) => {
        const timer = this.armed.get(tutorialType)
        if (timer !== undefined) {
            clearTimeout(timer)
        }
        this.armed.delete(tutorialType)
        this.retract(tutorialType, true)
    }

    isArmed = (tutorialType: EmberClueType): boolean => this.armed.has(tutorialType)

    isShowing = (tutorialType: EmberClueType): boolean => this.drawn.has(tutorialType)

    anyShowing = (): boolean => this.drawn.size > 0

    private retract = (tutorialType: EmberClueType, fade = false) => {
        const stop = this.drawn.get(tutorialType)
        this.drawn.delete(tutorialType)
        if (stop) {
            stop(fade ? { fade } : undefined)
        }
    }

    private refresh = (tutorialType: EmberClueType) => {
        if (!this.armed.has(tutorialType)) {
            return
        }
        const anchor =
            this.anchors.get(tutorialType) ||
            (this.props.builtInAnchor ? this.props.builtInAnchor(tutorialType) : undefined)
        if (anchor === undefined || anchor === null) {
            return
        }
        // Replace whatever was drawn for this clue before (a rebuilt panel) rather than stacking.
        this.retract(tutorialType)
        const stop = this.props.draw(tutorialType, anchor)
        if (stop) {
            this.drawn.set(tutorialType, stop)
        }
    }
}

export const SHELTER_CLUE_ITEMS = [
    { itemType: "planks", recipe: "ember-planks-recipe" },
    { itemType: "wall-wood", recipe: "ember-wall-recipe", pack: "ember-wall-pack", use: "ember-wall-use" },
    { itemType: "roof-wood", recipe: "ember-roof-recipe", pack: "ember-roof-pack", use: "ember-roof-use" },
] as const
